'use client';

import { forwardRef, ForwardRefRenderFunction, ReactNode, useEffect, useImperativeHandle, useRef } from 'react';
import { InputAreaInput, InputAreaInputProps } from './InputAreaInput';
import { InputFieldError } from '../InputField/InputFieldError';
import { InputFieldNote } from '../InputField/InputFieldNote';
import { InputFieldLabel } from '../InputField/InputFieldLabel';
import * as RadixForm from '@radix-ui/react-form';
import { twMerge } from '@tui-cruises/mein-schiff-web-tailwind-config';
export type InputAreaProps = Omit<InputAreaInputProps, 'required'> & {
  label?: ReactNode;
  note?: ReactNode;
  error?: string;

  /**
   * Use `label` if you don't want to add the `required` attribute to the textarea element itself.
   * This will avoid highlighting the textarea as invalid, when there is no value.
   * This is usually a user friendlier way than highlighting the field invalid on initial page load.
   */
  required?: boolean | 'label';
  asRadix?: boolean;
  radixFormMessages?: RadixForm.FormMessageProps[];
};
const InputAreaRenderFunction: ForwardRefRenderFunction<HTMLTextAreaElement, InputAreaProps> = (props, forwardedRef) => {
  const {
    label,
    note,
    error,
    className,
    asRadix,
    radixFormMessages,
    required,
    ...attrs
  } = props;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(forwardedRef, () => textareaRef.current);
  useEffect(() => {
    if (asRadix) {
      return;
    }
    textareaRef.current?.setCustomValidity(error ?? '');
  }, [asRadix, error]);
  if (!asRadix) {
    return <div className={twMerge('block space-y-1', className)}>
        <label className="block space-y-1">
          {label && <InputFieldLabel asChild>
              <div>
                {label}
                {required ? '*' : ''}
              </div>
            </InputFieldLabel>}
          <InputAreaInput {...attrs} required={typeof required === 'boolean' ? required : undefined} ref={textareaRef} />
          {!error && note && <InputFieldNote>{note}</InputFieldNote>}
        </label>

        {error && <InputFieldError>{error}</InputFieldError>}
      </div>;
  }
  return <RadixForm.Field name={attrs.name ?? ''} className={className} asChild data-sentry-element="RadixForm.Field" data-sentry-component="InputAreaRenderFunction" data-sentry-source-file="InputArea.tsx">
      <div className="space-y-1">
        <label className="block space-y-1">
          <RadixForm.Label asChild data-sentry-element="RadixForm.Label" data-sentry-source-file="InputArea.tsx">
            <InputFieldLabel asChild data-sentry-element="InputFieldLabel" data-sentry-source-file="InputArea.tsx">
              <div>
                {label}
                {required ? '*' : ''}
              </div>
            </InputFieldLabel>
          </RadixForm.Label>
          <RadixForm.Control asChild data-sentry-element="RadixForm.Control" data-sentry-source-file="InputArea.tsx">
            <InputAreaInput {...attrs} required={typeof required === 'boolean' ? required : undefined} data-sentry-element="InputAreaInput" data-sentry-source-file="InputArea.tsx" />
          </RadixForm.Control>
          <RadixForm.ValidityState data-sentry-element="RadixForm.ValidityState" data-sentry-source-file="InputArea.tsx">
            {validity => (validity?.valid ?? true) && <InputFieldNote>{note}</InputFieldNote>}
          </RadixForm.ValidityState>
        </label>

        {(radixFormMessages ?? []).map((props, idx) => <RadixForm.Message key={idx} {...props} asChild>
            <InputFieldError>{props.children}</InputFieldError>
          </RadixForm.Message>)}
      </div>
    </RadixForm.Field>;
};
export const InputArea = forwardRef<HTMLTextAreaElement, InputAreaProps>(InputAreaRenderFunction);